import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

import { Table, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_FINAL_ASSIGNMENT_API } from "../../../api/api";

import { IoSearchOutline } from "react-icons/io5";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

// import totalViewsImg from "../../../assets/tutor-student-dou-icon1.png";
// import avgCompletionImg from "../../../assets/tutor-student-dou-icon1.png";
// import totalstudentImg from "../../../assets/tutor-student-dou-icon1.png";

// latestIcons
import totalAssignments from "../../../assets/icons/student/assignment.svg";
import finalAssignments from "../../../assets/icons/admin/final-assig-submission.svg";
import pendingReview from "../../../assets/icons/admin/final-assign-pendi-review.svg";
import avgCompletion from "../../../assets/icons/admin/avg-final-assig-comp.svg";
// import avgCompletion from "../../../assets/icons/admin/avg-completion.svg";

import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminFinalAssignments.css";

const AdminFinalAssignments = () => {
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [assignmentStats, setAssignmentStats] = useState({});

  // search state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState("All Status");
  const [selectedGradeFilter, setSelectedGradeFilter] = useState("All Grades");

  //loading
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [issuingCertificateLoading, setIssuingCertificateLoading] =
    useState(null);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchFinalAssignment = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingAssignments(false);
      navigate("/admin-signin");

      return;
    }

    try {
      const response = await axios.get(
        ADMIN_FINAL_ASSIGNMENT_API.GET_FINAL_ASSIGNMENTS_BY_ADMIN,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setAssignments(data.data || []);
        setAssignmentStats(data.stats || {});
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setLoadingAssignments(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchFinalAssignment();
  }, [fetchFinalAssignment]);

  const handleIssueCertificate = async (finalAssignmentId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/admin-signin");
      return;
    }

    setIssuingCertificateLoading(finalAssignmentId);
    try {
      const response = await axios.post(
        ADMIN_FINAL_ASSIGNMENT_API.UPDATE_FINAL_ASSIGNMENT_BY_ADMIN,
        {
          final_assignment_id: finalAssignmentId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        await fetchFinalAssignment();
      } else {
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error issuing certificate:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setIssuingCertificateLoading(null);
    }
  };

  const filteredCoursesForDropdown = useMemo(() => {
    return [...new Set(assignments.map((a) => a.course_title))];
  }, [assignments]);

  const filteredStatusForDropdown = useMemo(() => {
    return [...new Set(assignments.map((a) => a.admin_status))];
  }, [assignments]);

  const filteredGradesForDropdown = useMemo(() => {
    return [...new Set(assignments.map((a) => a.grade))];
  }, [assignments]);

  const filteredAssignments = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return assignments.filter((s) => {
      return (
        (search === "" ||
          s.student_reg_number?.toString().toLowerCase().includes(searchText) ||
          s.student_name?.toLowerCase().includes(searchText) ||
          s.email?.toLowerCase().includes(searchText)) &&
        (selectedCourseFilter === "All Courses" ||
          s.course_title.includes(selectedCourseFilter)) &&
        (selectedStatusFilter === "All Status" ||
          s.admin_status === selectedStatusFilter) &&
        (selectedGradeFilter === "All Grades" ||
          s.grade === selectedGradeFilter)
      );
    });
  }, [
    assignments,
    search,
    selectedCourseFilter,
    selectedStatusFilter,
    selectedGradeFilter,
  ]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } =
    useSortableTable(filteredAssignments);

  const getGradeLabel = (grade) => {
    const g = grade?.toUpperCase();

    switch (g) {
      case "A":
        return "A (Above 75%)";
      case "B":
        return "B (Above 60%)";
      case "C":
        return "C (Above 50%)";
      case "F":
        return "F (Below 50%)";
      default:
        return grade || "-";
    }
  };

  // Pagination slice
  const rowsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const indexOfLastStudent = currentPage * rowsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - rowsPerPage;

  const currentAssignments = sortedData.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCourseFilter, selectedStatusFilter, selectedGradeFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  const clearFilters = () => {
    setSearch("");
    setSelectedCourseFilter("All Courses");
    setSelectedStatusFilter("All Status");
    setSelectedGradeFilter("All Grades");
    setCurrentPage(1);
  };

  const handleExportStudentAssignment = () => {
    const formattedData = sortedData.map((a) => ({
      Register_ID: a.student_reg_number || "N/A",
      Name: a.student_name,
      Email: a.email,
      Phone: a.phone_number,
      Batch: a.batch,
      Course: a.course_title,
      Assignment: a.assignment_title,
      Tutor: a.tutor_name,
      "Submitted Date": a.admin_submitted_at,
      Score: a.total_marks,
      Grade: a.grade,
      Status: a.admin_status,
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Final_Assignments",
      sheetName: "Assignment",
    });
  };

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  if (loadingAssignments) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="admin-assignment-management-wrapper">
      <h4 className="admin-dashboard-title">Final Assignment Management</h4>
      <p className="admin-dashboard-subtitle">
        Review all student final assignments, monitor tutor approval status, and
        manage admin approval for certification
      </p>

      <Row className="admin-assignment-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-assignment-section-stat-card">
            <h6 className="admin-assignment-section-stat-title">
              Total Final Assignments
            </h6>
            <img
              src={totalAssignments}
              className="admin-assignment-stat-icon"
              alt="assignments"
            />
            <h3 className="admin-assignment-section-stat-value">
              {Number(assignmentStats.total_assignments) || "-"}
            </h3>
            <span className="admin-assignment-section-stat-sub">
              +12% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-assignment-section-stat-card">
            <h6 className="admin-assignment-section-stat-title">
              Final Assignments Submission
            </h6>
            <img
              src={finalAssignments}
              className="admin-assignment-stat-icon"
              alt="assignments"
            />
            <h3 className="admin-assignment-section-stat-value">
              {Number(assignmentStats.completed_assignments) || "-"}
            </h3>
            <span className="admin-assignment-section-stat-sub">
              +12% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-assignment-section-stat-card">
            <h6 className="admin-assignment-section-stat-title">
              Final Assignments Pending Review
            </h6>
            <img
              src={pendingReview}
              className="admin-assignment-stat-icon"
              alt="review"
            />
            <h3 className="admin-assignment-section-stat-value">
              {Number(assignmentStats.pending_review) || "-"}
            </h3>
            <span className="admin-assignment-section-stat-sub">
              +3% vs last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-assignment-section-stat-card">
            <h6 className="admin-assignment-section-stat-title">
              Avg. Final Assignments Completion
            </h6>
            <img
              src={avgCompletion}
              className="admin-assignment-stat-icon"
              alt="avg"
            />
            <h3 className="admin-assignment-section-stat-value">
              {assignmentStats.completion_percentage || "-"}
            </h3>
            <span className="admin-assignment-section-stat-sub">
              +8% w/o churn
            </span>
          </div>
        </Col>
      </Row>

      <div className="admin-assignment-management-filters">
        <Row className="mb-0 admin-assignment-management-filters-rowsss">
          <Col md={4}>
            <div className="admin-assignment-management-search-box">
              <span className="admin-assignment-management-search-icon">
                <IoSearchOutline />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by enroll id, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-assignment-management-search-input"
              />
            </div>
          </Col>

          <Col md={2}>
            <Form.Select
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
            >
              <option value="All Courses">All Courses</option>
              {filteredCoursesForDropdown.map((course, index) => (
                <option key={index} value={course}>
                  {course}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* Progress */}
          <Col md={2}>
            <Form.Select
              value={selectedGradeFilter}
              onChange={(e) => setSelectedGradeFilter(e.target.value)}
            >
              <option value="All Grades">All Grades</option>
              {filteredGradesForDropdown.map((grade, idx) => (
                <option key={idx} value={grade}>
                  {getGradeLabel(grade)}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
            >
              <option value="All Status">All Status</option>
              {filteredStatusForDropdown.map((status, index) => (
                <option key={index} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={1}>
            <Button
              className="admin-assignment-management-clear-btn"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Col>

          <Col md={1}>
            <Button
              size="sm"
              className="admin-student-management-table-export"
              onClick={handleExportStudentAssignment}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-student-management-table-export-excel-img"
              />
            </Button>
          </Col>
        </Row>
      </div>

      {/* TABLE */}
      <div className="admin-assignment-management-table-wrapper">
        <Table className="admin-assignment-management-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("student_reg_number")}
                style={{ cursor: "pointer" }}
              >
                Enroll Id {renderSortIcon("student_reg_number")}
              </th>
              <th
                onClick={() => handleSort("student_name")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Student {renderSortIcon("student_name")}
                </div>
              </th>
              <th
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email {renderSortIcon("email")}
              </th>
              <th>Phone</th>
              <th>Batch</th>
              <th
                onClick={() => handleSort("course_title")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Course {renderSortIcon("course_title")}
                </div>
              </th>
              <th>Assignment</th>
              <th
                onClick={() => handleSort("tutor_name")}
                style={{ cursor: "pointer" }}
              >
                Tutor {renderSortIcon("tutor_name")}
              </th>
              <th
                onClick={() => handleSort("admin_submitted_at")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Submitted {renderSortIcon("admin_submitted_at")}{" "}
                </div>
              </th>
              <th
                onClick={() => handleSort("total_marks")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Score {renderSortIcon("total_marks")}{" "}
                </div>
              </th>
              <th
                onClick={() => handleSort("grade")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Grade {renderSortIcon("grade")}{" "}
                </div>
              </th>
              <th
                onClick={() => handleSort("admin_status")}
                style={{ cursor: "pointer" }}
              >
                Status {renderSortIcon("admin_status")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {/* {loadingAssignments ? (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  Loading assignments...
                </td>
              </tr>
            ) :  */}
            {currentAssignments.length > 0 ? (
              currentAssignments.map((a) => (
                <tr key={a.final_assignment_id}>
                  <td title={a.student_reg_number}>
                    {a.student_reg_number || "-"}
                  </td>
                  <td title={a.student_name}>{a.student_name || "-"}</td>
                  <td title={a.email}>{a.email || "-"}</td>
                  <td title={a.phone_number}>{a.phone_number || "-"}</td>
                  <td title={a.batch}>{a.batch || "-"}</td>
                  <td title={a.course_title}>{a.course_title || "-"}</td>
                  <td title={a.assignment_title}>
                    {a.assignment_title || "-"}
                  </td>
                  <td title={a.tutor_name}>{a.tutor_name || "-"}</td>
                  <td title={a.admin_submitted_at}>
                    {a.admin_submitted_at || "-"}
                  </td>

                  <td title={a.total_marks}>
                    {a.total_marks !== null ? (
                      <span
                        className={`admin-assignment-management-score-badge ${
                          Number(a.total_marks) > 75
                            ? "score-high"
                            : "admin-assignment-management-score-low"
                        }`}
                      >
                        {a.total_marks}
                      </span>
                    ) : (
                      "--"
                    )}
                  </td>

                  <td title={a.grade}>{a.grade}</td>
                  <td title={a.admin_status}>
                    <span
                      className={`admin-assignment-management-status-badge ${
                        a.admin_status?.toLowerCase() === "submitted"
                          ? "admin-assignment-management-status-submitted"
                          : a.admin_status?.toLowerCase() === "tutor pending"
                            ? "admin-assignment-management-status-pending"
                            : "admin-assignment-management-status-default"
                      }`}
                    >
                      {a.admin_status}
                    </span>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      className={`admin-final-assignment-issue-certificate-btn ${
                        a.admin_status === "Pending"
                          ? "admin-final-assignment-issue-enabled"
                          : "admin-final-assignment-issue-disabled"
                      }`}
                      disabled={
                        a.admin_status !== "Pending" ||
                        issuingCertificateLoading === a.final_assignment_id
                      }
                      onClick={() =>
                        a.admin_status === "Pending" &&
                        handleIssueCertificate(a.final_assignment_id)
                      }
                    >
                      {issuingCertificateLoading === a.final_assignment_id
                        ? "Issuing..."
                        : a.admin_status === "Submitted"
                          ? "Certificate Issued"
                          : "Issue Certificate"}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p className="admin-dashboard-no-data">No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminFinalAssignments;
